import { Component, computed, effect, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  InfiniteScrollCustomEvent,
  IonBackButton,
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonItem,
  IonLabel,
  IonList,
  IonMenuButton,
  IonPopover,
  IonSearchbar,
  IonText,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';

import { DatePipe, NgClass } from '@angular/common';
import { ApiService } from '@libs/core/base-config';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { VxDynamicImageComponent, VxImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { withOrdersListCustomers } from '@libs/mtjr/shared/generated/entities/order/order-store-features';
import { getProductById } from '@libs/mtjr/shared/generated/entities/product/service/product.service';
import { Entity, SubOrderStatusList } from '@libs/mtjr/shared/generated/types';

import { CartItem, CartStore } from '@apps/mtjr/customers/app/cart/cart.store';
import { signalStore, withComputed } from '@ngrx/signals';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

const emptyVariationId = '000000000000000000000000';
type OrderInfo = Record<string, { orderProducts: Entity.OrderProduct[]; orderTotal: number; totalItems: number }>;

export const OrdersStore = signalStore(
  withOrdersListCustomers(),

  withComputed((store) => ({
    orderInfo: computed(() => {
      const state = store.ordersListCustomersState();
      if (state.success) {
        return state.data.orders.reduce((acc: OrderInfo, order) => {
          const orderProducts = order.subOrders.reduce((subAcc: Entity.OrderProduct[], subOrder) => {
            return subAcc.concat(subOrder.orderProducts);
          }, [] as Entity.OrderProduct[]);

          const orderTotal = order.subOrders.reduce((total, subOrder) => {
            return total + subOrder.total;
          }, 0);

          const totalItems = order.subOrders.reduce((total, subOrder) => {
            return (
              total +
              subOrder.orderProducts.reduce((subTotal, item) => {
                return subTotal + item.quantity;
              }, 0)
            );
          }, 0);

          acc[order.id] = {
            orderProducts,
            orderTotal,
            totalItems,
          };
          return acc;
        }, {} as OrderInfo);
      }
      return {} as OrderInfo;
    }),
  })),
);

@Component({
  selector: 'mac-orders',
  providers: [OrdersStore],
  imports: [
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonBackButton,
    IonContent,
    IonSearchbar,
    IonCard,
    IonText,
    IonLabel,
    IonButton,
    IonIcon,
    RouterLink,
    IonInfiniteScroll,
    IonInfiniteScrollContent,
    VxCurrencyPipe,
    IonList,
    IonPopover,
    VxLoadingComponent,
    VxErrorComponent,
    IonItem,
    VxImageComponent,
    VxDynamicImageComponent,
    NgClass,
    TranslatePipe,
    IonMenuButton,
    DatePipe,
  ],
  templateUrl: 'orders.page.html',
})
export class OrdersPage {
  store = inject(OrdersStore);
  orderStatusList = SubOrderStatusList;
  route = inject(ActivatedRoute);
  router = inject(Router);
  eventBus = inject(NgEventBus);
  cartStore = inject(CartStore);
  appAlerts = inject(AppAlerts);
  apiService = inject(ApiService);

  searchValue = signal<string | undefined>(undefined);

  constructor() {
    effect(() => {
      const query = this.store.ordersListCustomersInput();
      this.router.navigate([], {
        queryParams: {
          status: query.status,
          search: query.search ? query.search : undefined,
        },
      });
    });

    this.route.queryParams.subscribe((params) => {
      const { status, search } = params;
      this.searchValue.set(search);
      const query = this.store.ordersListCustomersInput();
      if (
        this.store.ordersListCustomersState().type !== 'initial' &&
        status === query.status &&
        search === query.search
      )
        return;

      this.store.ordersListCustomersPage({ status, search, totalPerPage: 7 });
    });

    this.eventBus.on('orders:fetch').subscribe(() => {
      this.store.ordersListCustomersPage({ page: 1 });
    });
  }

  getProducts(order: Entity.Order): Entity.OrderProduct[] {
    return order.subOrders.reduce<Entity.OrderProduct[]>((acc, e) => acc.concat(e.orderProducts), []);
  }

  private async getReorderCartItems(orderProducts: Entity.OrderProduct[]): Promise<CartItem[]> {
    const productCache = new Map<string, Entity.Product>();
    const cartItems: CartItem[] = [];

    for (const orderProduct of orderProducts) {
      let product = productCache.get(orderProduct.productId);
      if (!product) {
        product = await firstValueFrom(getProductById(this.apiService, { id: orderProduct.productId }));
        productCache.set(orderProduct.productId, product);
      }

      const variationId = orderProduct.variationId;
      const hasVariation = variationId !== undefined && variationId !== null && variationId !== emptyVariationId;
      const variation = hasVariation ? product.variations?.find((item) => item.id === variationId) : undefined;
      if (hasVariation && !variation) {
        throw new Error(`Variation ${variationId} no longer exists for product ${orderProduct.productId}`);
      }

      cartItems.push({
        product,
        variation,
        quantity: orderProduct.quantity,
      });
    }

    return cartItems;
  }

  async onIonInfinite(event: InfiniteScrollCustomEvent) {
    await this.store.ordersListCustomersFetchNextPage();
    await event?.target?.complete();
  }

  handleSearchInput(event?: CustomEvent) {
    const search = event?.detail?.value;
    if (this.store.ordersListCustomersInput().search === search) return;
    this.store.ordersListCustomersPage({ search, page: 1 });
  }

  async reorder(order: Entity.Order, event: Event) {
    event.stopPropagation();
    event.preventDefault();

    const confirm = await this.appAlerts.yesNoAlertTR({
      header: 'ORDERS.REORDER',
      message: 'ORDERS.REORDER_CONFIRM',
      yesCaption: 'COMMON.YES',
      noCaption: 'COMMON.NO',
    });

    if (!confirm) return;

    try {
      const orderProducts = this.getProducts(order);
      const cartItems = await this.getReorderCartItems(orderProducts);

      this.cartStore.reset();
      for (const item of cartItems) {
        this.cartStore.addItem(item);
      }

      await this.appAlerts.successSnackbarTR('ORDERS.REORDER_SUCCESS');
      this.router.navigate(['/cart']);
    } catch (error) {
      console.error('Error reordering:', error);
      await this.appAlerts.dangerSnackbarTR('ORDERS.REORDER_ERROR');
    }
  }
}
